import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, XCircle, Clock, MapPin, User, Ticket, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short', year: 'numeric', month: 'short',
      day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function TicketCheckin() {
  const { qrToken } = useParams();
  const [ticket, setTicket]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [checking, setChecking] = useState(false);
  const [alreadyDone, setAlreadyDone] = useState(false);

  useEffect(() => {
    if (!qrToken) { setError('Missing QR token'); setLoading(false); return; }
    fetch(`${API_URL}/api/public/event-payments/checkin/${qrToken}`)
      .then(async r => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Ticket not found');
        setTicket(d);
        if (d.checked_in) setAlreadyDone(true);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [qrToken]);

  const handleCheckIn = async () => {
    setChecking(true);
    try {
      const res = await fetch(`${API_URL}/api/public/event-payments/checkin/${qrToken}`, { method: 'POST' });
      const data = await res.json();
      if (res.status === 409) {
        // Already checked in
        setAlreadyDone(true);
        setTicket(prev => ({ ...prev, checked_in: true, checked_in_at: data.detail?.split('at ')[1] || prev.checked_in_at }));
        return;
      }
      if (!res.ok) { setError(data.detail || 'Check-in failed'); return; }
      setTicket(prev => ({ ...prev, checked_in: true, checked_in_at: data.checked_in_at }));
      setAlreadyDone(false); // fresh success
    } catch {
      setError('Network error — please try again');
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-sm text-center shadow-xl">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Invalid QR Code</h1>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  // Determine current state
  const isPaid       = ticket.payment_status === 'paid';
  const isCheckedIn  = ticket.checked_in;
  const justCheckedIn = isCheckedIn && !alreadyDone;

  return (
    <>
      <Helmet><title>Ticket Check-in — {ticket.event_title}</title></Helmet>
      <div className={`min-h-screen flex flex-col items-center justify-center p-4 transition-colors duration-500 ${
        justCheckedIn ? 'bg-emerald-600' : isCheckedIn && alreadyDone ? 'bg-amber-500' : 'bg-slate-900'
      }`}>
        <div className="w-full max-w-sm">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {justCheckedIn ? (
              <CheckCircle className="w-20 h-20 text-white drop-shadow-lg" data-testid="checkin-success-icon" />
            ) : isCheckedIn ? (
              <AlertTriangle className="w-20 h-20 text-white drop-shadow-lg" data-testid="checkin-already-icon" />
            ) : !isPaid ? (
              <XCircle className="w-20 h-20 text-red-400 drop-shadow-lg" data-testid="checkin-unpaid-icon" />
            ) : (
              <Ticket className="w-20 h-20 text-white drop-shadow-lg" data-testid="checkin-ready-icon" />
            )}
          </div>

          {/* Status banner */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className={`px-6 py-4 text-white ${
              justCheckedIn ? 'bg-emerald-600' :
              isCheckedIn   ? 'bg-amber-500' :
              !isPaid       ? 'bg-red-500' :
              'bg-indigo-600'
            }`}>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80" data-testid="checkin-event-name">
                {ticket.event_title}
              </p>
              <h1 className="text-xl font-bold mt-0.5" data-testid="checkin-status-heading">
                {justCheckedIn ? 'Checked In!' :
                 isCheckedIn   ? 'Already Checked In' :
                 !isPaid       ? 'Unpaid Ticket' :
                 'Ready to Check In'}
              </h1>
            </div>

            {/* Ticket details */}
            <div className="px-6 py-5 space-y-3">
              <div className="flex items-start gap-3" data-testid="checkin-buyer-name">
                <User className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Guest</p>
                  <p className="font-semibold text-slate-900">{ticket.buyer_name}</p>
                  <p className="text-xs text-slate-400">{ticket.buyer_email}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Ticket className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Ticket</p>
                  <p className="font-semibold text-slate-900">{ticket.ticket_name}</p>
                  <p className="text-sm text-slate-500">{ticket.quantity} {ticket.quantity === 1 ? 'person' : 'people'}</p>
                </div>
              </div>

              {ticket.event_start && (
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Date & Time</p>
                    <p className="text-sm text-slate-700">{fmtDate(ticket.event_start)}</p>
                  </div>
                </div>
              )}

              {ticket.event_location && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">Location</p>
                    <p className="text-sm text-slate-700">{ticket.event_location}</p>
                  </div>
                </div>
              )}

              {isCheckedIn && ticket.checked_in_at && (
                <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5 mt-1" data-testid="checkin-timestamp">
                  <p className="text-xs text-amber-700 font-medium">Checked in at {fmtDate(ticket.checked_in_at)}</p>
                </div>
              )}

              {!isPaid && (
                <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-2.5 mt-1" data-testid="checkin-unpaid-msg">
                  <p className="text-xs text-red-700 font-medium">Payment not confirmed — entry not permitted.</p>
                </div>
              )}
            </div>

            {/* Action */}
            <div className="px-6 pb-6">
              {!isCheckedIn && isPaid && (
                <button
                  onClick={handleCheckIn}
                  disabled={checking}
                  className="w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base transition-colors disabled:opacity-60"
                  data-testid="checkin-confirm-btn"
                >
                  {checking ? 'Checking in…' : `Check In (${ticket.quantity} ${ticket.quantity === 1 ? 'person' : 'people'})`}
                </button>
              )}
              {isCheckedIn && (
                <div className="w-full py-3.5 rounded-xl bg-slate-100 text-slate-500 font-semibold text-base text-center" data-testid="checkin-done-badge">
                  Entry Recorded
                </div>
              )}
            </div>
          </div>

          {/* Ticket ID footer */}
          <p className="text-center text-xs text-white/40 mt-4" data-testid="checkin-ticket-id">
            Ticket #{ticket.ticket_id?.slice(-8).toUpperCase()}
          </p>
        </div>
      </div>
    </>
  );
}
